// ── useReverbChannel ────────────────────────────────────────
// Owns the Echo lifecycle for an authenticated user session.
//
// Mount point: inside AppLayout (which sits behind ProtectedRoute), so the
// hook only ever runs for an authenticated user. Pitfall 7 guard on
// `user?.id` is belt-and-braces for the auth-bootstrap race + logout teardown.
//
// Responsibilities:
//   1. Subscribe to `private-user.{user.id}` on mount (Echo prefixes "private-"
//      under the hood — we pass `user.{id}` to `echo.private()`).
//   2. Bind listeners for `.agent.thinking`, `.vision.generation_succeeded`,
//      `.vision.generation_failed` — leading-dot prefix matches the
//      broadcastAs() short names (Pitfall 8). Phase 6 does NOT render payload
//      bodies into the DOM (T-06-09: rich rendering belongs to Phase 9); the
//      side-effect for Phase 6 is the sidebar dot + cursor advancement.
//   3. On pusher:disconnected → flip status to "polling" and immediately fire
//      a catchup poll, then poll /api/interaction-logs every 3s (D-04: no
//      backoff — polling starts the moment WS drops).
//   4. On pusher:connected → flip status to "connected", run exactly one
//      catchup poll, clear the interval (D-04).
//   5. On unmount (logout/route teardown) → clear interval, leave the channel,
//      and destroyEcho() so a stale WS doesn't outlive the session.
//
// In-memory cursor only (D-01) — no localStorage; refresh starts fresh.

import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api-client';
import { getEcho, destroyEcho } from '@/lib/echo';

export type ReverbStatus = 'connecting' | 'connected' | 'polling';

interface LastSeenCursor {
  ts: string;
  id: string;
}

interface InteractionLogRow {
  id: string;
  created_at: string;
}

interface InteractionLogsResponse {
  rows: InteractionLogRow[];
}

const POLL_INTERVAL_MS = 3000; // D-04: 3s cadence on disconnect (no backoff).
const POLL_LIMIT = 50;

/**
 * Optional configuration for useReverbChannel.
 *
 * `seedOnMount` (default `false`): on first mount, fetch the most recent
 * N=50 rows of the user's transcript to seed `lastSeen`. CONTEXT D-01 calls
 * for this seed on fresh login, but it is OPT-IN here because no Phase 6
 * consumer currently needs it (Phase 9 owns the message surface). Enabling
 * this requires a server-side "latest N" endpoint shape that Phase 6's
 * forward-only ASC cursor does not provide; leave `false` until that
 * endpoint lands.
 */
export interface UseReverbChannelOptions {
  seedOnMount?: boolean;
  /**
   * Optional callback invoked when an `.agent.message.delta` frame arrives —
   * one cumulative-text chunk of a streamed agent reply (Phase 09.1, STR-03).
   * Frames are keyed by `streamId`; `body` is the whole reply-so-far (D-01),
   * `seq` is monotonic from 1 and the consumer drops any frame whose `seq` is
   * <= the highest seen (D-03).
   */
  onAgentDelta?: (payload: AgentDeltaPayload) => void;
  /**
   * Optional callback invoked when an `.agent.message.complete` frame arrives —
   * the single completion event for a streamed reply AND for a non-streamed
   * crisis/pause/fixed reply (D-12). Carries the canonical `interactionLogId`
   * so the consumer can re-key the live bubble from `streamId` to it.
   *
   * NOTE: the Laravel event was renamed `agent.message` -> `agent.message.complete`
   * in Plan 09.1-03; this binding replaces the old `.agent.message` listener.
   */
  onAgentComplete?: (payload: AgentCompletePayload) => void;
  /**
   * Optional callback invoked when an `.agent.message.failed` frame arrives —
   * the agent turn could not be delivered (terminal forward failure, or a
   * Python mid-stream empty-crash). Lets the consumer clear the thinking
   * affordance and offer a calm resend (D-09). `streamId` is null when no
   * stream was ever minted (Python never ran).
   */
  onAgentFailed?: (payload: AgentFailedPayload) => void;
  /**
   * Optional callback invoked when an `.agent.thinking` heartbeat arrives.
   * Phase 8 uses it to drive the "Becoming denkt nach …" affordance.
   */
  onAgentThinking?: () => void;
  /**
   * Optional callback invoked when a `.vision.generation_succeeded` frame
   * arrives — one generated vision image has been produced and registered
   * (Phase 10, VIS-04/VIS-05). The Vision Gallery refreshes off this; the
   * full-screen CandidateReveal advances its candidate tiles off it.
   */
  onVisionSucceeded?: (payload: VisionSucceededPayload) => void;
  /**
   * Optional callback invoked when a `.vision.generation_failed` frame
   * arrives — a vision generation attempt failed (Phase 10, VIS-06). The
   * SPA surfaces the calm, budget-reassuring retry affordance off this.
   */
  onVisionFailed?: (payload: VisionFailedPayload) => void;
  /**
   * Optional callback invoked the moment the WebSocket disconnects (Phase 09.1).
   * The chat panel uses it to flip a live streaming bubble into the
   * `reconnecting` state so a mid-stream socket drop keeps the partial text
   * visible while the polling fallback reconciles the canonical row (D-13).
   */
  onDisconnected?: () => void;
}

/**
 * Payload of the `agent.message.delta` broadcast — see AgentMessageDelta.php.
 * `body` is CUMULATIVE (the whole reply-so-far, D-01); `seq` is monotonic
 * from 1 per turn (D-03).
 */
export interface AgentDeltaPayload {
  streamId: string;
  conversationId: string;
  seq: number;
  body: string;
}

/**
 * Payload of the `agent.message.complete` broadcast — see AgentMessageEmitted.php.
 * `streamId` ties the completion back to the live streaming bubble; `body` is
 * the canonical reply text persisted to interaction_logs.
 */
export interface AgentCompletePayload {
  streamId: string;
  conversationId: string;
  interactionLogId: string;
  body: string;
}

/** Payload of the `agent.message.failed` broadcast — see AgentMessageFailed.php. */
export interface AgentFailedPayload {
  streamId: string | null;
  conversationId: string;
}

/**
 * Payload of the `vision.generation_succeeded` broadcast — see
 * VisionGenerationSucceeded.php. One produced + registered vision image.
 */
export interface VisionSucceededPayload {
  userId: string;
  mediaRegistryId: string;
  r2ObjectKey: string;
}

/**
 * Payload of the `vision.generation_failed` broadcast — see VisionGenerationFailed.php.
 *
 * `mediaRegistryId` was on the wire from Phase 5 and undeclared here until Plan
 * 14-14: `broadcastWith()` has always emitted it, and the reveal needs it to
 * tell a failure belonging to the set it is showing from one belonging to some
 * other generation. Declaring it did not change the broadcast.
 *
 * Optional rather than required because a listener that only wants "something
 * failed" should not have to care, and because narrowing an existing wire
 * contract in TypeScript is the kind of change that fails at runtime rather
 * than at build time if the assumption is ever wrong.
 */
export interface VisionFailedPayload {
  userId: string;
  mediaRegistryId?: string;
}

export function useReverbChannel(options: UseReverbChannelOptions = {}) {
  const {
    seedOnMount = false,
    onAgentDelta,
    onAgentComplete,
    onAgentFailed,
    onAgentThinking,
    onVisionSucceeded,
    onVisionFailed,
    onDisconnected,
  } = options;
  // Keep the latest callbacks in refs so the channel subscription effect does
  // not re-run (and re-subscribe) every time a parent re-renders with a fresh
  // closure. The effect depends only on user.id.
  const onAgentDeltaRef = useRef(onAgentDelta);
  const onAgentCompleteRef = useRef(onAgentComplete);
  const onAgentFailedRef = useRef(onAgentFailed);
  const onAgentThinkingRef = useRef(onAgentThinking);
  const onVisionSucceededRef = useRef(onVisionSucceeded);
  const onVisionFailedRef = useRef(onVisionFailed);
  const onDisconnectedRef = useRef(onDisconnected);
  onAgentDeltaRef.current = onAgentDelta;
  onAgentCompleteRef.current = onAgentComplete;
  onAgentFailedRef.current = onAgentFailed;
  onAgentThinkingRef.current = onAgentThinking;
  onVisionSucceededRef.current = onVisionSucceeded;
  onVisionFailedRef.current = onVisionFailed;
  onDisconnectedRef.current = onDisconnected;
  const { user } = useAuth();
  const [status, setStatus] = useState<ReverbStatus>('connecting');
  const lastSeen = useRef<LastSeenCursor | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  // BL-01: track whether we have ever observed a WS disconnect in this
  // session. On the initial WS `connected` event (first mount), this is
  // `false` and we MUST NOT run catchup() — `lastSeen` is null and the
  // server scope is forward-only ASC, so a no-cursor fetch returns the
  // OLDEST 50 rows for the user. That poisons the cursor for the rest of
  // the session and the polling fallback walks forward from the bottom of
  // history instead of from the disconnect boundary. Only run catchup()
  // from `onConnected` when this flag is true (true reconnect after a gap).
  const hadDisconnect = useRef(false);

  useEffect(() => {
    // Pitfall 7: guard on `user.id` (string), not the `user` object — the
    // object identity changes on every AuthContext refresh, which would cause
    // a reconnect storm if we depended on `user`.
    if (!user?.id) return;

    const echo = getEcho();
    const channelName = `user.${user.id}`;
    const channel = echo.private(channelName);

    // Pitfall 8: the leading "." is required for broadcastAs() short names.
    // Without it, Echo prefixes the app namespace and the listener silently
    // never fires.
    channel.listen('.agent.thinking', (_payload: { ts: string; sequence: number; demo: boolean }) => {
      // T-06-09: Phase 6 rendered nothing here. Phase 8 (Plan 08-07) opts in
      // via onAgentThinking to drive the "Becoming denkt nach …" affordance.
      onAgentThinkingRef.current?.();
    });
    // Phase 09.1 (Plan 09.1-04): the agent reply now STREAMS. Each
    // `.agent.message.delta` carries a cumulative-text chunk keyed by
    // streamId; `.agent.message.complete` finalizes the turn (this binding
    // REPLACES the old `.agent.message` listener — the Laravel event was
    // renamed in Plan 09.1-03); `.agent.message.failed` signals an
    // undeliverable turn. Leading-dot prefix is mandatory (Pitfall 8).
    channel.listen('.agent.message.delta', (payload: AgentDeltaPayload) => {
      onAgentDeltaRef.current?.(payload);
    });
    channel.listen('.agent.message.complete', (payload: AgentCompletePayload) => {
      onAgentCompleteRef.current?.(payload);
    });
    channel.listen('.agent.message.failed', (payload: AgentFailedPayload) => {
      onAgentFailedRef.current?.(payload);
    });
    // Phase 10 (Plan 10-05): the vision-generation listeners now drive real
    // UI — the Vision Gallery refreshes and the CandidateReveal advances off
    // `.vision.generation_succeeded`; `.vision.generation_failed` surfaces the
    // calm budget-reassuring retry affordance. Leading-dot prefix mandatory.
    channel.listen(
      '.vision.generation_succeeded',
      (payload: VisionSucceededPayload) => {
        onVisionSucceededRef.current?.(payload);
      },
    );
    channel.listen('.vision.generation_failed', (payload: VisionFailedPayload) => {
      onVisionFailedRef.current?.(payload);
    });

    // Reverb connector exposes the underlying pusher-js connection via
    // `connector.pusher`. The double cast keeps the call site honest about
    // the runtime shape while accepting Echo's loose types.
    const pusher = (
      echo.connector as unknown as {
        pusher: { connection: { bind: (evt: string, cb: () => void) => void } };
      }
    ).pusher;

    async function catchup() {
      const params = new URLSearchParams({ limit: String(POLL_LIMIT) });
      if (lastSeen.current) {
        params.set('after_ts', lastSeen.current.ts);
        params.set('after_id', lastSeen.current.id);
      }
      try {
        const resp = await api.get<InteractionLogsResponse>(
          `/api/interaction-logs?${params.toString()}`,
        );
        const rows = resp.rows ?? [];
        if (rows.length > 0) {
          const last = rows[rows.length - 1];
          lastSeen.current = { ts: last.created_at, id: last.id };
        }
      } catch {
        // Swallow polling failures — the next interval retries. Observability
        // belongs to a future plan; spamming console.error here is just noise.
      }
    }

    const onConnected = () => {
      setStatus('connected');
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
      // BL-01: only run catchup() when this is a TRUE reconnect after a
      // disconnect gap. On the very first WS `connected` event of the
      // session, lastSeen is null and the forward-only ASC scope would
      // return the OLDEST 50 rows, poisoning the cursor for the rest of
      // the session.
      if (hadDisconnect.current) {
        hadDisconnect.current = false;
        void catchup();
      }
    };

    const onDisconnected = () => {
      hadDisconnect.current = true;
      // Phase 09.1: let the consumer flip a live streaming bubble into the
      // reconnecting state before the polling fallback reconciles it (D-13).
      onDisconnectedRef.current?.();
      setStatus('polling');
      if (pollTimer.current === null) {
        // D-04: start polling IMMEDIATELY — no backoff before the first poll.
        void catchup();
        pollTimer.current = setInterval(() => {
          void catchup();
        }, POLL_INTERVAL_MS);
      }
    };

    pusher.connection.bind('connected', onConnected);
    pusher.connection.bind('disconnected', onDisconnected);

    // BL-01: opt-in seed of the most recent N rows on mount (CONTEXT D-01).
    // Off by default in Phase 6 — see UseReverbChannelOptions docblock for
    // why this is gated. When enabled, this is a SEPARATE call from the
    // reconnect catchup() and must not flip `hadDisconnect`.
    if (seedOnMount) {
      void catchup();
    }

    return () => {
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
      echo.leave(channelName);
      destroyEcho();
    };
    // Pitfall 7: depend on `user.id` (stable string), not the `user` object.
  }, [user?.id]);

  return { status } as const;
}
